/**
 * biomes.ts — ground-layer painting for the Latium landscape.
 *
 * Uses the Unreal landscape layer vocabulary (Grass, ForestGround, Dirt, Mud,
 * HeavyMud, PlowedSoil, StonyGround, Fields, Wetness) and the owner-locked
 * restrained olive/earth palette:
 *   near meadow (0.25, 0.34, 0.18) / near dry (0.38, 0.31, 0.18)
 * (V013 second-pass tints, sRGB 0..1).
 *
 * Pure functions over the sim Heightfield: deterministic, render-free math
 * except for the final THREE.Color conversion helper.
 */
import * as THREE from "three";
import { Heightfield, clamp01, type FieldPlot } from "../../sim/pcg";

/** sRGB palette entries (0..1). */
export const PALETTE = {
  nearMeadow: [0.25, 0.34, 0.18] as const,
  nearDry: [0.38, 0.31, 0.18] as const,
  forestGround: [0.19, 0.17, 0.12] as const,
  dirt: [0.36, 0.28, 0.18] as const,
  mud: [0.23, 0.18, 0.12] as const,
  heavyMud: [0.17, 0.13, 0.09] as const,
  plowedSoil: [0.25, 0.18, 0.12] as const,
  stonyGround: [0.44, 0.42, 0.36] as const,
  wheat: [0.55, 0.47, 0.25] as const,
  vineRow: [0.2, 0.26, 0.13] as const,
  riverBed: [0.33, 0.3, 0.23] as const,
} as const;

type RGB = readonly [number, number, number];

function lerp3(a: RGB, b: RGB, t: number): [number, number, number] {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
}

/** Deterministic 2D hash for high-frequency ground detail (no sim state). */
function detailHash(x: number, z: number): number {
  let h = Math.imul(Math.floor(x * 3.1), 0x27d4eb2d) ^ Math.imul(Math.floor(z * 3.1), 0x165667b1);
  h = Math.imul(h ^ (h >>> 15), 0x85ebca6b);
  h ^= h >>> 13;
  return ((h >>> 0) % 1000) / 1000;
}

export interface GroundSample {
  color: [number, number, number];
  /** 0..1 wetness darkening already applied. */
  wet: number;
  field: FieldPlot | null;
}

/**
 * Sample the painted ground layer stack at (x, z).
 * h and slope should come from field.heightAt / field.slopeAt.
 */
export function sampleGround(
  field: Heightfield,
  x: number,
  z: number,
  h: number,
  slope: number,
): GroundSample {
  const moisture = field.moistureAt(x, z);
  const woodland = field.woodlandAt(x, z);
  const riverDist = field.riverDistAt(x, z);
  const plot = field.fieldAt(x, z);
  const dither = detailHash(x, z);

  // Base: dry straw <-> olive meadow by moisture + patchiness.
  const patch = detailHash(x * 0.11 + 7.7, z * 0.11 - 3.3);
  const green = clamp01(0.3 + moisture * 0.7 - patch * 0.3);
  let col = lerp3(PALETTE.nearDry, PALETTE.nearMeadow, green);

  // Dirt patches on open ground.
  const dirtT = clamp01((detailHash(x * 0.05 + 40, z * 0.05) - 0.62) * 4) * (1 - woodland);
  col = lerp3(col, PALETTE.dirt, dirtT * 0.7);

  // Stony ground on steeps and scree bands.
  const stony = clamp01((slope - 0.42) * 3.2 + (detailHash(x * 0.09 - 11, z * 0.09 + 5) - 0.66) * 2.4);
  col = lerp3(col, PALETTE.stonyGround, clamp01(stony) * 0.85);

  // Forest floor: dark humus under canopy (kept translucent so ground shows through).
  col = lerp3(col, PALETTE.forestGround, clamp01(woodland * 1.15) * 0.68);

  // River margins: mud -> heavy mud -> bed.
  if (riverDist < 26) {
    const mudT = clamp01(1 - riverDist / 26);
    col = lerp3(col, PALETTE.mud, mudT * 0.8);
  }
  if (riverDist < 12) {
    const heavyT = clamp01(1 - riverDist / 12);
    col = lerp3(col, PALETTE.heavyMud, heavyT * 0.85);
  }

  // Fields: crop-specific painting.
  if (plot) {
    if (plot.crop === "wheat") {
      // Golden wheat with faint plow-row striping along the plot's local x.
      const c = Math.cos(-plot.rot);
      const s = Math.sin(-plot.rot);
      const lx = (x - plot.cx) * c - (z - plot.cz) * s;
      const rows = 0.5 + 0.5 * Math.sin(lx * 2.4);
      col = lerp3(PALETTE.plowedSoil, PALETTE.wheat, 0.55 + rows * 0.3);
    } else if (plot.crop === "vines") {
      const c = Math.cos(-plot.rot);
      const s = Math.sin(-plot.rot);
      const lx = (x - plot.cx) * c - (z - plot.cz) * s;
      const rows = 0.5 + 0.5 * Math.sin(lx * 3.1);
      col = rows > 0.55 ? [...PALETTE.vineRow] : [...PALETTE.plowedSoil];
    } else {
      // Olive grove: tilled soil; trees are planted by the scatter pass.
      col = [...PALETTE.plowedSoil];
    }
  }

  // Wetness: darken toward water.
  const wet = clamp01(1 - riverDist / 30);
  const wetDarken = 1 - wet * 0.38;
  col = [col[0] * wetDarken, col[1] * wetDarken, col[2] * wetDarken];

  // Submerged bed tint (below typical water line of the carve).
  if (h < -2.5) {
    col = lerp3(col, PALETTE.riverBed, clamp01((-2.5 - h) / 4) * 0.7);
  }

  // Per-texel dither breaks up banding at grazing angles.
  const d = 0.96 + dither * 0.08;
  return { color: [col[0] * d, col[1] * d, col[2] * d], wet, field: plot };
}

const _c = new THREE.Color();
/** Convert a sampled sRGB ground color into three.js working-space color. */
export function groundColorToThree(
  sample: [number, number, number],
  out: THREE.Color = _c,
): THREE.Color {
  return out.setRGB(sample[0], sample[1], sample[2], THREE.SRGBColorSpace);
}
